import { useState, useEffect, useRef } from "react";
import { Plus, Trash2, Play, Camera, Image, X, Video, Youtube, Upload, Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "@/context/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper function to get full image URL - handles all URL formats
const getImageUrl = (url) => {
  if (!url) return '';
  
  // If it's a relative URL, prepend the backend URL
  if (url.startsWith('/api/')) {
    return `${BACKEND_URL}${url}`;
  }
  
  // If it's an old preview URL, replace with current backend URL
  if (url.includes('.preview.emergentagent.com') || url.includes('.emergent.host')) {
    const path = url.replace(/https?:\/\/[^\/]+/, '');
    return `${BACKEND_URL}${path}`;
  }
  
  // Otherwise return as is (external URLs)
  return url;
};

export default function Activism() {
  const { isAdmin } = useAuth();
  const [photos, setPhotos] = useState([]);
  const [videos, setVideos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [addPhotoOpen, setAddPhotoOpen] = useState(false);
  const [addVideoOpen, setAddVideoOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [previewUrls, setPreviewUrls] = useState([]);
  const [videoUploadType, setVideoUploadType] = useState("youtube");
  const fileInputRef = useRef(null);
  const videoInputRef = useRef(null);
  
  const [newVideo, setNewVideo] = useState({
    title: "",
    description: "",
    youtube_url: "",
    video_url: "",
    is_upload: false
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [photosRes, videosRes] = await Promise.all([
        axios.get(`${API}/activism/photos`),
        axios.get(`${API}/activism/videos`)
      ]);
      setPhotos(photosRes.data);
      setVideos(videosRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  // Multi-photo selection handler
  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    
    const validFiles = [];
    const previews = [];
    
    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        toast.error(`${file.name} is too large. Maximum size is 10MB.`);
        continue;
      }
      if (!file.type.startsWith("image/")) {
        toast.error(`${file.name} is not an image file.`);
        continue;
      }
      validFiles.push(file);
      previews.push(URL.createObjectURL(file));
    }
    
    if (validFiles.length > 0) {
      setSelectedFiles(validFiles);
      setPreviewUrls(previews);
      toast.success(`${validFiles.length} photo(s) selected! Tap 'Upload Photos' to add them.`);
    }
  };

  const removeSelectedFile = (index) => {
    const newFiles = [...selectedFiles];
    const newPreviews = [...previewUrls];
    newFiles.splice(index, 1);
    newPreviews.splice(index, 1);
    setSelectedFiles(newFiles);
    setPreviewUrls(newPreviews);
  };

  // Multi-photo upload handler
  const handleUploadPhotos = async (e) => {
    e.preventDefault();
    if (selectedFiles.length === 0) {
      toast.error("Please select photos to upload");
      return;
    }
    
    setUploading(true);
    setUploadProgress(0);
    
    const uploadedPhotos = [];
    const totalFiles = selectedFiles.length;
    
    try {
      for (let i = 0; i < selectedFiles.length; i++) {
        const file = selectedFiles[i];
        const formData = new FormData();
        formData.append("file", file);
        formData.append("title", file.name.split('.')[0]);
        formData.append("description", "");
        
        const uploadRes = await axios.post(`${API}/upload/image`, formData, {
          headers: { "Content-Type": "multipart/form-data" },
          timeout: 60000
        });
        
        const photoData = {
          title: file.name.split('.')[0],
          description: "",
          image_url: uploadRes.data.url
        };
        
        const response = await axios.post(`${API}/activism/photos`, photoData);
        uploadedPhotos.push(response.data);
        
        setUploadProgress(Math.round(((i + 1) / totalFiles) * 100));
      }
      
      setPhotos([...uploadedPhotos.reverse(), ...photos]);
      resetPhotoForm();
      toast.success(`Successfully uploaded ${uploadedPhotos.length} photo(s)!`);
    } catch (error) {
      console.error("Upload error:", error);
      toast.error(error.response?.data?.detail || "Failed to upload photos");
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const resetPhotoForm = () => {
    setSelectedFiles([]);
    setPreviewUrls([]);
    setAddPhotoOpen(false);
    setUploadProgress(0);
  };

  // Video upload from phone
  const handleVideoFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    if (file.size > 100 * 1024 * 1024) {
      toast.error("Video file too large. Maximum size is 100MB.");
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("title", newVideo.title || file.name.split('.')[0]);
      formData.append("description", newVideo.description || "");
      
      const uploadRes = await axios.post(`${API}/upload/video`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000
      });
      
      const videoData = {
        title: newVideo.title || file.name.split('.')[0],
        description: newVideo.description || "",
        video_url: uploadRes.data.url,
        is_upload: true
      };
      
      const response = await axios.post(`${API}/activism/videos`, videoData);
      setVideos([response.data, ...videos]);
      resetVideoForm();
      toast.success("Video uploaded successfully!");
    } catch (error) {
      console.error("Video upload error:", error);
      toast.error("Failed to upload video");
    } finally {
      setUploading(false);
    }
  };

  // YouTube video handler
  const handleAddYouTubeVideo = async (e) => {
    e.preventDefault();
    if (!newVideo.youtube_url) {
      toast.error("Please enter a YouTube URL");
      return;
    }
    
    setUploading(true);
    try {
      const response = await axios.post(`${API}/activism/videos`, {
        title: newVideo.title || "Activism Video",
        description: newVideo.description || "",
        youtube_url: newVideo.youtube_url
      });
      setVideos([response.data, ...videos]);
      resetVideoForm();
      toast.success("Video added successfully!");
    } catch (error) {
      toast.error("Failed to add video");
    } finally {
      setUploading(false);
    }
  };

  // Video URL handler (for external video links)
  const handleAddVideoURL = async (e) => {
    e.preventDefault();
    if (!newVideo.video_url) {
      toast.error("Please enter a video URL");
      return;
    }
    
    setUploading(true);
    try {
      const response = await axios.post(`${API}/activism/videos`, {
        title: newVideo.title || "Activism Video",
        description: newVideo.description || "",
        video_url: newVideo.video_url,
        is_upload: true
      });
      setVideos([response.data, ...videos]);
      resetVideoForm();
      toast.success("Video added successfully!");
    } catch (error) {
      toast.error("Failed to add video");
    } finally {
      setUploading(false);
    }
  };

  const resetVideoForm = () => {
    setNewVideo({ title: "", description: "", youtube_url: "", video_url: "", is_upload: false });
    setAddVideoOpen(false);
    setVideoUploadType("youtube");
  };

  const handleDeletePhoto = async (photoId) => {
    if (!window.confirm("Delete this photo?")) return;
    try {
      await axios.delete(`${API}/activism/photos/${photoId}`);
      setPhotos(photos.filter(p => p.id !== photoId));
      toast.success("Photo deleted");
    } catch (error) {
      toast.error("Failed to delete photo");
    }
  };

  const handleTogglePhotoFeatured = async (photoId) => {
    try {
      const response = await axios.patch(`${API}/activism/photos/${photoId}/feature`);
      setPhotos(photos.map(p => 
        p.id === photoId ? { ...p, is_featured: response.data.is_featured } : p
      ));
      toast.success(response.data.is_featured ? "Photo featured!" : "Photo unfeatured");
    } catch (error) {
      toast.error("Failed to update featured status");
    }
  };

  const handleDeleteVideo = async (videoId) => {
    if (!window.confirm("Delete this video?")) return;
    try {
      await axios.delete(`${API}/activism/videos/${videoId}`);
      setVideos(videos.filter(v => v.id !== videoId));
      toast.success("Video deleted");
    } catch (error) {
      toast.error("Failed to delete video");
    }
  };

  const handleToggleVideoFeatured = async (videoId) => {
    try {
      const response = await axios.patch(`${API}/activism/videos/${videoId}/feature`);
      setVideos(videos.map(v => 
        v.id === videoId ? { ...v, is_featured: response.data.is_featured } : v
      ));
      toast.success(response.data.is_featured ? "Video featured!" : "Video unfeatured");
    } catch (error) {
      toast.error("Failed to update featured status");
    }
  };

  // Sort items with featured first
  const sortedPhotos = [...photos].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
  const sortedVideos = [...videos].sort((a, b) => (b.is_featured ? 1 : 0) - (a.is_featured ? 1 : 0));
  const featuredPhotosCount = photos.filter(p => p.is_featured).length;
  const featuredVideosCount = videos.filter(v => v.is_featured).length;

  return (
    <div data-testid="activism-page">
      {/* Hero Section */}
      <section className="gradient-hero py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto">
            <span className="inline-block bg-[#0056D2]/10 text-[#0056D2] px-4 py-2 rounded-full text-sm font-semibold mb-6">
              Take Action
            </span>
            <h1 className="text-4xl sm:text-5xl font-bold font-['Outfit'] text-[#0F172A] mb-6">
              Activism & Advocacy
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              Join us in our mission to create lasting change. Through peaceful activism and community engagement, 
              we work together to build a safer, more just society for all.
            </p>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-8 bg-white border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap justify-center gap-8">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center">
                <Image className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{photos.length}</p>
                <p className="text-sm text-slate-500">Photos</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-red-100 rounded-lg flex items-center justify-center">
                <Video className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{videos.length}</p>
                <p className="text-sm text-slate-500">Videos</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center">
                <Star className="w-5 h-5 text-amber-600" fill="currentColor" />
              </div>
              <div>
                <p className="text-2xl font-bold text-[#0F172A]">{featuredPhotosCount + featuredVideosCount}</p>
                <p className="text-sm text-slate-500">Featured</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Gallery Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-[#0F172A]">
                Activism Photos
              </h2>
              <p className="text-slate-600 mt-2">Photos from our advocacy events and community actions</p>
            </div>
            
            {/* Add Photo Dialog */}
            <Dialog open={addPhotoOpen} onOpenChange={(open) => { if (!open) resetPhotoForm(); else setAddPhotoOpen(true); }}>
              <DialogTrigger asChild>
                <Button className="bg-[#0056D2] hover:bg-[#0046B2] text-white rounded-full" data-testid="add-activism-photo-btn">
                  <Camera className="w-4 h-4 mr-2" />
                  Add Photos
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Outfit']">Upload Activism Photos</DialogTitle>
                </DialogHeader>
                
                <form onSubmit={handleUploadPhotos} className="space-y-4 mt-4">
                  <div>
                    <Label className="text-slate-700">Select Photos</Label>
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      multiple
                      onChange={handleFileSelect}
                      className="hidden"
                      id="activism-photo-upload"
                    />
                    <label 
                      htmlFor="activism-photo-upload"
                      className="mt-2 flex items-center justify-center gap-2 bg-[#0056D2] hover:bg-[#0046B2] text-white py-4 px-6 rounded-xl cursor-pointer font-semibold transition-colors active:bg-[#003d99] touch-manipulation"
                    >
                      <Camera className="w-5 h-5" />
                      {selectedFiles.length > 0 ? `${selectedFiles.length} Photo(s) Selected - Tap to Add More` : "Choose Photos from Phone"}
                    </label>
                    
                    {previewUrls.length > 0 && (
                      <div className="mt-4 p-3 bg-slate-100 rounded-xl">
                        <div className="grid grid-cols-4 gap-1 mb-2 max-h-24 overflow-hidden">
                          {previewUrls.slice(0, 8).map((url, index) => (
                            <div key={index} className="relative">
                              <img src={url} alt={`Preview ${index + 1}`} className="w-full h-12 object-cover rounded" />
                              <button
                                type="button"
                                onClick={() => removeSelectedFile(index)}
                                className="absolute -top-1 -right-1 bg-red-500 rounded-full p-0.5"
                              >
                                <X className="w-2 h-2 text-white" />
                              </button>
                            </div>
                          ))}
                        </div>
                        {previewUrls.length > 8 && (
                          <p className="text-xs text-slate-500 text-center">+{previewUrls.length - 8} more photos</p>
                        )}
                        <p className="text-xs text-green-600 text-center">Ready to upload {selectedFiles.length} photo(s)</p>
                        
                        {uploading && uploadProgress > 0 && (
                          <div className="mt-2">
                            <div className="w-full bg-slate-300 rounded-full h-2">
                              <div className="bg-[#0056D2] h-2 rounded-full transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                            </div>
                            <p className="text-xs text-center text-slate-500 mt-1">Uploading... {uploadProgress}%</p>
                          </div>
                        )}
                      </div>
                    )}
                    
                    {previewUrls.length === 0 && (
                      <p className="text-xs text-slate-500 text-center mt-2">Select multiple photos - Max 10MB each</p>
                    )}
                  </div>

                  <Button 
                    type="submit" 
                    className="w-full bg-green-600 hover:bg-green-700 py-4 text-lg font-bold" 
                    disabled={uploading || selectedFiles.length === 0}
                    data-testid="upload-activism-photos-btn"
                  >
                    {uploading ? `Uploading... ${uploadProgress}%` : `Upload ${selectedFiles.length || ''} Photo${selectedFiles.length !== 1 ? 's' : ''}`}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {/* Photos Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-[#0056D2] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : photos.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {sortedPhotos.map((photo) => (
                <div 
                  key={photo.id} 
                  className={`relative aspect-square bg-white rounded-xl overflow-hidden group cursor-pointer shadow-sm hover:shadow-md transition-shadow ${photo.is_featured ? 'ring-2 ring-amber-400' : ''}`}
                  onClick={() => setSelectedPhoto(photo)}
                  data-testid={`activism-photo-${photo.id}`}
                >
                  {/* Featured Badge */}
                  {photo.is_featured && (
                    <div className="absolute top-2 left-2 z-10 bg-amber-400 text-amber-900 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                      <Star className="w-3 h-3" fill="currentColor" />
                      Featured
                    </div>
                  )}
                  <img 
                    src={getImageUrl(photo.image_url)} 
                    alt={photo.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity">
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <h3 className="text-white font-medium text-sm line-clamp-2">{photo.title}</h3>
                    </div>
                  </div>
                  {/* Action buttons - Admin Only */}
                  {isAdmin && (
                    <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={(e) => { e.stopPropagation(); handleTogglePhotoFeatured(photo.id); }}
                        className={`p-1.5 rounded-full transition-colors ${photo.is_featured ? 'bg-amber-400 text-amber-900' : 'bg-black/50 text-white hover:bg-amber-400 hover:text-amber-900'}`}
                        title={photo.is_featured ? "Remove from featured" : "Add to featured"}
                      >
                        <Star className="w-4 h-4" fill={photo.is_featured ? "currentColor" : "none"} />
                      </button>
                      <button
                        onClick={(e) => { e.stopPropagation(); handleDeletePhoto(photo.id); }}
                        className="p-1.5 bg-black/50 rounded-full text-white hover:bg-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-white rounded-2xl border border-slate-200">
              <Image className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-2">No Photos Yet</h3>
              <p className="text-slate-500 mb-6">Upload photos from your activism events and advocacy work.</p>
              <Button onClick={() => setAddPhotoOpen(true)} className="bg-[#0056D2] hover:bg-[#0046B2]">
                <Plus className="w-5 h-5 mr-2" />
                Add First Photo
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Video Gallery Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-10">
            <div>
              <h2 className="text-2xl md:text-3xl font-bold font-['Outfit'] text-[#0F172A]">
                Activism Videos
              </h2>
              <p className="text-slate-600 mt-2">Videos documenting our advocacy and community work</p>
            </div>
            
            {/* Add Video Dialog */}
            <Dialog open={addVideoOpen} onOpenChange={(open) => { if (!open) resetVideoForm(); else setAddVideoOpen(true); }}>
              <DialogTrigger asChild>
                <Button className="bg-red-600 hover:bg-red-700 text-white rounded-full" data-testid="add-activism-video-btn">
                  <Video className="w-4 h-4 mr-2" />
                  Add Video
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-white border-slate-200 max-w-lg max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                  <DialogTitle className="font-['Outfit']">Add Activism Video</DialogTitle>
                </DialogHeader>
                
                <Tabs value={videoUploadType} onValueChange={setVideoUploadType} className="mt-4">
                  <TabsList className="grid w-full grid-cols-3">
                    <TabsTrigger value="youtube" className="flex items-center gap-2">
                      <Youtube className="w-4 h-4" />
                      YouTube
                    </TabsTrigger>
                    <TabsTrigger value="url" className="flex items-center gap-2">
                      <Plus className="w-4 h-4" />
                      Video URL
                    </TabsTrigger>
                    <TabsTrigger value="upload" className="flex items-center gap-2">
                      <Upload className="w-4 h-4" />
                      Upload
                    </TabsTrigger>
                  </TabsList>
                  
                  <TabsContent value="youtube" className="space-y-4 mt-4">
                    <div>
                      <Label>Video Title</Label>
                      <Input
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                        placeholder="Enter video title"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>YouTube URL *</Label>
                      <Input
                        value={newVideo.youtube_url}
                        onChange={(e) => setNewVideo({ ...newVideo, youtube_url: e.target.value })}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="mt-1"
                        required
                      />
                      <p className="text-xs text-slate-500 mt-1">Paste a YouTube video link</p>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={newVideo.description}
                        onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                        placeholder="Brief description of the video"
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <Button 
                      onClick={handleAddYouTubeVideo} 
                      className="w-full bg-red-600 hover:bg-red-700" 
                      disabled={uploading}
                    >
                      {uploading ? "Adding..." : "Add YouTube Video"}
                    </Button>
                  </TabsContent>

                  <TabsContent value="url" className="space-y-4 mt-4">
                    <div>
                      <Label>Video Title</Label>
                      <Input
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                        placeholder="Enter video title"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Video URL *</Label>
                      <Input
                        value={newVideo.video_url}
                        onChange={(e) => setNewVideo({ ...newVideo, video_url: e.target.value })}
                        placeholder="https://example.com/video.mp4"
                        className="mt-1"
                        required
                      />
                      <p className="text-xs text-slate-500 mt-1">Direct link to video file (MP4, WebM, etc.) or embed URL</p>
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={newVideo.description}
                        onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                        placeholder="Brief description of the video"
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <Button 
                      onClick={handleAddVideoURL} 
                      className="w-full bg-red-600 hover:bg-red-700" 
                      disabled={uploading || !newVideo.video_url}
                    >
                      {uploading ? "Adding..." : "Add Video"}
                    </Button>
                  </TabsContent>
                  
                  <TabsContent value="upload" className="space-y-4 mt-4">
                    <div>
                      <Label>Video Title</Label>
                      <Input
                        value={newVideo.title}
                        onChange={(e) => setNewVideo({ ...newVideo, title: e.target.value })}
                        placeholder="Enter video title"
                        className="mt-1"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Textarea
                        value={newVideo.description}
                        onChange={(e) => setNewVideo({ ...newVideo, description: e.target.value })}
                        placeholder="Brief description of the video"
                        className="mt-1"
                        rows={3}
                      />
                    </div>
                    <div>
                      <input
                        ref={videoInputRef}
                        type="file"
                        accept="video/*"
                        onChange={handleVideoFileSelect}
                        className="hidden"
                        id="activism-video-upload"
                      />
                      <label
                        htmlFor="activism-video-upload"
                        className="flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 text-white py-4 px-6 rounded-xl cursor-pointer font-semibold transition-colors active:bg-red-800 touch-manipulation w-full"
                      >
                        <Video className="w-5 h-5" />
                        {uploading ? "Uploading..." : "Select Video from Phone"}
                      </label>
                      <p className="text-xs text-slate-500 text-center mt-2">MP4, MOV, AVI, WebM - Max 100MB</p>
                    </div>
                  </TabsContent>
                </Tabs>
              </DialogContent>
            </Dialog>
          </div>

          {/* Videos Grid */}
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="w-10 h-10 border-4 border-red-600 border-t-transparent rounded-full animate-spin" />
            </div>
          ) : videos.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {sortedVideos.map((video) => (
                <div 
                  key={video.id}
                  className={`bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-lg transition-shadow group ${video.is_featured ? 'ring-2 ring-amber-400' : ''}`}
                  data-testid={`activism-video-${video.id}`}
                >
                  <div className="relative aspect-video bg-slate-100">
                    {/* Featured Badge */}
                    {video.is_featured && (
                      <div className="absolute top-2 left-2 z-10 bg-amber-400 text-amber-900 px-2 py-1 rounded-full text-xs font-bold flex items-center gap-1">
                        <Star className="w-3 h-3" fill="currentColor" />
                        Featured
                      </div>
                    )}
                    {video.is_upload ? (
                      <video 
                        src={getImageUrl(video.video_url)}
                        className="w-full h-full object-cover"
                        controls
                      />
                    ) : (
                      <>
                        <img 
                          src={video.thumbnail_url} 
                          alt={video.title}
                          className="w-full h-full object-cover"
                        />
                        <a
                          href={video.youtube_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="absolute inset-0 flex items-center justify-center bg-black/30 group-hover:bg-black/40 transition-colors"
                        >
                          <div className="w-16 h-16 bg-red-600 rounded-full flex items-center justify-center group-hover:scale-110 transition-transform">
                            <Play className="w-8 h-8 text-white ml-1" fill="white" />
                          </div>
                        </a>
                      </>
                    )}
                    {/* Action buttons - Admin Only */}
                    {isAdmin && (
                      <div className="absolute top-2 right-2 flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <button
                          onClick={() => handleToggleVideoFeatured(video.id)}
                          className={`p-2 rounded-full transition-colors ${video.is_featured ? 'bg-amber-400 text-amber-900' : 'bg-black/50 text-white hover:bg-amber-400 hover:text-amber-900'}`}
                          title={video.is_featured ? "Remove from featured" : "Add to featured"}
                        >
                          <Star className="w-4 h-4" fill={video.is_featured ? "currentColor" : "none"} />
                        </button>
                        <button
                          onClick={() => handleDeleteVideo(video.id)}
                          className="p-2 bg-black/50 rounded-full text-white hover:bg-red-500 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="font-semibold text-[#0F172A] line-clamp-2">{video.title}</h3>
                    {video.description && (
                      <p className="text-sm text-slate-500 mt-1 line-clamp-2">{video.description}</p>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16 bg-slate-50 rounded-2xl">
              <Video className="w-16 h-16 text-slate-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold text-[#0F172A] mb-2">No Videos Yet</h3>
              <p className="text-slate-500 mb-6">Add YouTube videos or upload directly from your phone.</p>
              <Button onClick={() => setAddVideoOpen(true)} className="bg-red-600 hover:bg-red-700">
                <Plus className="w-5 h-5 mr-2" />
                Add First Video
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* Call to Action Section */}
      <section className="py-16 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-gradient-to-br from-[#0056D2] to-[#0044A5] rounded-3xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold font-['Outfit'] text-white mb-6">
              Get Involved in Our Cause
            </h2>
            <p className="text-lg text-blue-100 max-w-2xl mx-auto mb-8">
              Your voice matters. Join us in our peaceful activism efforts to create positive change in Milwaukee and beyond.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a href="/volunteer">
                <Button className="bg-white text-[#0056D2] hover:bg-blue-50 rounded-full px-8 py-3 font-semibold">
                  Become a Volunteer
                </Button>
              </a>
              <a href="/donate">
                <Button className="bg-[#F59E0B] text-white hover:bg-[#D97706] rounded-full px-8 py-3 font-semibold">
                  Support Our Mission
                </Button>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Photo Lightbox */}
      <Dialog open={!!selectedPhoto} onOpenChange={() => setSelectedPhoto(null)}>
        <DialogContent className="max-w-4xl p-0 bg-black border-none overflow-hidden">
          <DialogTitle className="sr-only">{selectedPhoto?.title}</DialogTitle>
          {selectedPhoto && (
            <div>
              <img 
                src={getImageUrl(selectedPhoto.image_url)} 
                alt={selectedPhoto.title}
                className="w-full max-h-[80vh] object-contain"
              />
              <div className="p-4 bg-black/80">
                <h3 className="text-lg font-semibold text-white">{selectedPhoto.title}</h3>
                {selectedPhoto.description && (
                  <p className="text-sm text-slate-300 mt-1">{selectedPhoto.description}</p>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
